import { api } from '../../core/network/api';

export interface GoldPackage {
  id: string;
  name: string;
  priceCents: number;
  bars: number;
  tag: string | null;
  best: boolean;
}

export interface CheckoutResult {
  /** Provider hosted-checkout URL (redirect here for a real gateway). */
  url: string;
  /** Dev mock settles inline — when true, just refetch the balance. */
  completed: boolean;
  bars: number;
}

/** Custom-amount buy terms (buy any bar amount at the base rate). */
export interface CustomTerms {
  minBars: number;
  maxBars: number;
  centsPerBar: number;
}

/** A discounted limited-time bundle (dominion's special offers). */
export interface SpecialOffer {
  id: string;
  name: string;
  bars: number;
  priceCents: number;
  originalCents: number;
  savings: number;
  tag: 'FLASH' | 'HOT' | 'DEAL' | 'LIMITED' | string;
  color: string;
}

/** KYC purchase gate — spends ≥ threshold need an APPROVED verification when enabled. */
export interface KycTerms {
  enabled: boolean;
  thresholdCents: number;
  status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
}

export interface PackagesView {
  packages: GoldPackage[];
  offers: SpecialOffer[];
  /** Epoch ms when the current offer slate rotates (fresh deals every 8h). */
  offersEndAt: number;
  /** True only while the mock payment provider is active. Gates every
   *  "no real charge is made" line — with ConvertIQ configured the card IS
   *  charged and the storefront must not say otherwise (QA 86cb9yd3p item 4). */
  testMode: boolean;
  custom: CustomTerms;
  kyc: KycTerms;
}

/** GET /api/topup/packages — gold-bar packs + special offers + custom terms. */
export const fetchPackages = () => api.get<PackagesView>('/topup/packages');

/** POST /api/topup/checkout — a fixed package, a special offer, OR a custom bar amount.
 *  `currency` tells the backend (and ConvertIQ) which currency to charge.       */
export const topupCheckout = (body: { packageId?: string; offerId?: string; bars?: number; currency?: string }) =>
  api.post<CheckoutResult>('/topup/checkout', body);

export interface PurchaseEntry {
  id: string;
  pack: string;
  bars: number;
  amountCents: number;
  /** The currency this payment was actually charged in — frozen, not the display pick. */
  currency: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | string;
  createdAt: string;
}

/** GET /api/topup/history — this player's purchase ledger (newest first). */
export const fetchTopupHistory = () => api.get<PurchaseEntry[]>('/topup/history');

export interface InvoiceEntry {
  invoiceNumber: string;
  description: string;
  totalCents: number;
  currency: string;
  status: string;
  date: string;
  downloadUrl: string;
}

/** GET /api/topup/invoices — this player's invoices (newest first). */
export const fetchInvoices = () => api.get<InvoiceEntry[]>('/topup/invoices');

/** Browser URL for an invoice PDF (auth rides on the access cookie). */
export const invoicePdfUrl = (invoiceNumber: string) =>
  `/api/topup/invoices/${encodeURIComponent(invoiceNumber)}/download`;

/**
 * Fetch an invoice PDF and hand it to the browser as a download.
 *
 * This used to be a plain `<a href target="_blank">`, which is why QA hit a raw
 * `401 {"statusCode":401,…}` page (2026-08-17): a browser navigation does not go
 * through the axios client, so the 15-minute access cookie expiring could not be
 * repaired by the refresh interceptor — the player just got JSON. Here the 401 is
 * handled explicitly: refresh once (the same rotation the interceptor performs),
 * then retry. Anything still failing is reported to the caller for a toast.
 */
export async function downloadInvoicePdf(invoiceNumber: string): Promise<void> {
  const url = invoicePdfUrl(invoiceNumber);
  const hit = () => fetch(url, { credentials: 'include', headers: { Accept: 'application/pdf' } });

  let res = await hit();
  if (res.status === 401) {
    await api.post('/auth/refresh');
    res = await hit();
  }
  if (!res.ok) throw new Error(`invoice download failed (${res.status})`);

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser time to start the save before the blob is reclaimed.
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

// ── Transaction ledger — every cash/gold movement (buys, sells, rewards…) ──
export interface LedgerEntry {
  id: string;
  entryType: 'CREDIT' | 'DEBIT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | string;
  currency: 'SOFT' | 'PREMIUM';
  /** Signed — credits positive, debits negative. */
  amount: number;
  balanceAfter: number;
  description: string;
  referenceId: string | null;
  createdAt: string;
}

/** GET /api/wallet/ledger — this player's full transaction history (newest first). */
export const fetchLedger = (params?: { limit?: number; currency?: 'SOFT' | 'PREMIUM' }) => {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.currency) q.set('currency', params.currency);
  const qs = q.toString();
  return api.get<LedgerEntry[]>(`/wallet/ledger${qs ? `?${qs}` : ''}`);
};
